import { Component, Element, Event, EventEmitter, h, Listen, Method, Prop, State } from '@stencil/core';
import Bind from 'bind-decorator';
import { AriaObject } from '../../utils/aria-object';
import { generateCssClasses } from '../../utils/generate-css-classes';

@Component({
	shadow: true,
	styleUrl: 'zds-input-search.scss',
	tag: 'zds-input-search',
})
export class ZdsInputSearch {
	inputElement: HTMLZdsInputTextElement;
	wrapperElement: HTMLDivElement;
	collapsibleIconElement: HTMLZdsButtonElement;
	expandendWidth: number;
	collapsedWidth: number;

	@Element() hostElement: HTMLElement;

	@Event() zdsBlur: EventEmitter;
	@Event() zdsChange: EventEmitter<string>;
	@Event() zdsClick: EventEmitter<string>; // Internal; passed from input-text
	@Event() zdsFocus: EventEmitter;
	@Event() zdsSearch: EventEmitter<string>;

	@Prop() componentTabIndex: number = 0;
	@Prop() clearable: boolean = true;
	@Prop() collapsible: boolean = false;
	@Prop() debounce: number = 300;
	@Prop() disabled: boolean;
	@Prop() inputAria: AriaObject | string;
	@Prop() fitHeight: boolean = false;
	@Prop() minSearchLength: number = 3;
	@Prop() label: string;
	@Prop() name: string;
	@Prop() placeholder: string;
	@Prop() readonly: boolean;
	@Prop({ mutable: true, reflect: true }) value: string;

	@State() collapsed: boolean = true;

	debounceTimer: number;

	@Listen('click', { target: 'body' })
	handleDocumentClick(event: MouseEvent) {
		if (!this.collapsible) {
			return;
		}

		const clickedElement = event.target as HTMLElement;

		/**
		 * Collapse the input if the user clicks outside of it and it has no value.
		 */
		if (this.inputElement && !this.inputElement.contains(clickedElement) && !this.value && !this.collapsed) {
			this.collapsed = true;
			return;
		}

		/**
		 * Blur the input if the user clicks outside of it and contains value.
		 */
		this.inputElement.blurComponent();
	}

	@Method()
	async focusComponent() {
		this.inputElement.focusComponent();
	}

	@Method()
	async reset() {
		await this.inputElement.reset();
	}

	@Bind
	onIconClick() {
		this.collapsed = !this.collapsed;
		this.inputElement.focusComponent();
	}

	@Bind
	onInput(event: CustomEvent) {
		this.value = (event.target as HTMLInputElement).value;

		if (this.debounceTimer !== null) {
			clearTimeout(this.debounceTimer);
			this.debounceTimer = null;
		}

		this.debounceTimer = window.setTimeout(() => {
			if (this.value.length > 0 && this.value.length < this.minSearchLength) {
				return;
			}

			this.debounceTimer = null;
			this.zdsSearch.emit(this.value);
		}, this.debounce);
	}

	componentWillLoad() {
		if (!this.collapsible || this.value) {
			this.collapsed = false;
		}
	}

	/**
	 * If the input is not collapsable, treat it like a normal input.
	 *
	 * If it's collapsable, check for Initial Value. If it has, input starts expanded and without focus.
	 */
	componentDidLoad() {
		if (!this.collapsible) {
			return;
		}

		this.expandendWidth = this.wrapperElement.getBoundingClientRect().width;
		this.collapsedWidth = this.collapsibleIconElement.getBoundingClientRect().width;

		if (this.collapsed) {
			this.wrapperElement.style.width = `${this.collapsedWidth}px`;
		}

		// wait for the initial width to be set before apllying transition effects
		requestAnimationFrame(() => {
			this.wrapperElement.style.transition = 'var(--zds-internal-input-search-transition)';
		});
	}

	renderCollapsibleIcon() {
		if (!this.collapsible) {
			return;
		}

		const iconClasses = generateCssClasses('zds-input-search__icon', {
			collapsed: this.collapsed,
		});

		return (
			<zds-button
				ref={(element) => (this.collapsibleIconElement = element)}
				class={iconClasses}
				variant="text"
				preset="muted"
				aspect-square
				pill
				onZdsClick={this.onIconClick}
			>
				<zds-icon library="fa-regular" name="magnifying-glass" size={4}></zds-icon>
			</zds-button>
		);
	}

	render() {
		const inputSearchClasses = generateCssClasses('zds-input-search', {
			collapsible: this.collapsible,
			collapsed: this.collapsed,
			fitHeight: this.fitHeight,
		});
		const inputWrapperClasses = generateCssClasses('zds-input-search__input-wrapper', {
			collapsed: this.collapsed,
			fitHeight: this.fitHeight,
		});

		const currentWidth = `${this.collapsed ? this.collapsedWidth : this.expandendWidth}px`;

		return (
			<div class={inputSearchClasses}>
				<div
					style={{ width: currentWidth }}
					class={inputWrapperClasses}
					ref={(element) => (this.wrapperElement = element)}
				>
					<zds-input-text
						type="search"
						componentTabIndex={this.componentTabIndex}
						disabled={this.disabled}
						inputAria={this.inputAria}
						fitHeight={this.fitHeight}
						label={this.label}
						name={this.name}
						onZdsInput={this.onInput}
						onZdsChange={this.onInput}
						placeholder={this.placeholder}
						readonly={this.readonly}
						value={this.value}
						clearable={true}
						ref={(element) => (this.inputElement = element)}
					>
						<zds-icon
							class="zds-input-search__input-icon"
							slot="input-start"
							name="magnifying-glass"
							library="fa-regular"
							size={4}
						></zds-icon>
						<slot name="label" slot="label" />
					</zds-input-text>
				</div>
				{this.renderCollapsibleIcon()}
			</div>
		);
	}
}
